import * as React from 'react';
import styled, { css } from 'styled-components';
import { RadioButtonOff, RadioButtonOn } from '../icons';
import { COLORS } from '../styles';

const StyledRadioButton = styled.div<{ margin?: string }>`
  display: flex;
  flex-direction: row;
  cursor: pointer;
  width: 100%;

  ${props => (props.margin ? `margin: ${props.margin}` : '')}

  svg {
    padding: 4px;
  }

  label {
    cursor: pointer;
  }
`;

const disabledState = css`
  box-shadow: none;
  cursor: not-allowed;
  svg {
    color: ${COLORS.neutral_300};
  }
  div {
    pointer-events: none;
  }
`;

const interactionStates = css`
  cursor: pointer;
  border-radius: 50%;
  &:hover {
    background-color: ${COLORS.primary_20};
    svg {
      color: ${COLORS.primary_700};
    }
  }

  &:focus {
    box-shadow: 0 0 8px 0 ${COLORS.toast_info};
    outline: none;
    svg {
      color: ${COLORS.neutral_600};
    }
  }

  &:active {
    background: ${COLORS.primary_100};
    box-shadow: none;
    svg {
      color: ${COLORS.primary_800};
    }
  }
`;

const RadioSection = styled.div<{ disabled?: boolean; showWarning?: boolean; selected?: boolean }>`
  width: 32px;
  height: 32px;
  margin: auto 14px auto 0;
  border-radius: 50%;
  svg {
    color: ${props => (props.selected ? COLORS.primary_500 : props.showWarning ? COLORS.warning_400 : COLORS.neutral_600)};
  }
  ${props => (props.disabled ? disabledState : interactionStates)}
`;

const TextSection = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  margin: auto 0;

  label {
    weight: 400;
    font-size: 18px;
    color: ${props => (props.disabled ? COLORS.neutral_300 : COLORS.black)};
  }
  span {
    weight: 400;
    font-size: 16px;
  }
`;

interface Props {
  id: string;
  selected: boolean;
  select: (selected: boolean) => void;
  label?: string;
  additionalLabel?: string;
  showWarning?: boolean;
  disabled?: boolean;
  margin?: string;
}

const RadioButton: React.FunctionComponent<Props> = ({ id, selected, label, additionalLabel, showWarning, select, disabled, margin }: Props) => {
  const onKeyPress = (e: any) => {
    if (e.keyCode === 13) {
      select(!selected);
    }
  };
  return (
    <StyledRadioButton key={id} margin={margin}>
      <RadioSection
        onClick={() => select(!selected)}
        onMouseDown={e => e.preventDefault()}
        id={id}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={onKeyPress}
        disabled={disabled}
        showWarning={showWarning}
        selected={selected}>
        {selected ? <RadioButtonOn size="24px" /> : <RadioButtonOff size="24px" />}
      </RadioSection>
      <TextSection onClick={() => select(!selected)} onMouseDown={e => e.preventDefault()} disabled={disabled}>
        {label && <label htmlFor={id}>{label}</label>}
        {additionalLabel !== undefined && <span>{additionalLabel}</span>}
      </TextSection>
    </StyledRadioButton>
  );
};

export default RadioButton;
